import { useCallback } from 'react';
import { getDevicePlatform } from '../lib/platform';
import {
  buildSystemBrowserUrl,
  getAbsoluteUrl,
  PROTOCOL_REGEX,
} from '../lib/url';
import {
  reportRoutingError,
  trackInAppNavigation,
} from '../lib/error-reporting';
import useIsInstagramBrowser from './useIsInstagramBrowser';

const SILENT_BLOCK_TIMEOUT_MS = 2000;

const useInAppBrowser = ({
  creatorName,
  preferInAppBrowser = false,
  onSilentNavigationBlock,
} = {}) => {
  const isInstagram = useIsInstagramBrowser();

  const handleLinkClick = useCallback(
    (url) => {
      if (typeof window === 'undefined') {
        return;
      }

      const targetUrl = getAbsoluteUrl(url);
      const hasProtocol = PROTOCOL_REGEX.test(targetUrl);
      const { isIOS, isAndroid, devicePlatformName } = getDevicePlatform();

      const openStandardLink = () => {
        if (isInstagram) {
          window.location.assign(targetUrl);
        } else {
          window.open(targetUrl, '_blank', 'noopener,noreferrer');
        }
      };

      const isUnsupportedEnvironment = !hasProtocol || (!isIOS && !isAndroid);

      if (preferInAppBrowser || isUnsupportedEnvironment) {
        openStandardLink();
        return;
      }

      const systemBrowserUrl = buildSystemBrowserUrl(targetUrl);
      let hasNavigationResolved = false;
      let silentNavigationBlockTimeoutId = null;

      const cleanupNavigationTrackers = () => {
        document.removeEventListener('visibilitychange', onAppBackgrounded);
        window.removeEventListener('pagehide', onPageUnloaded);
        if (silentNavigationBlockTimeoutId) {
          clearTimeout(silentNavigationBlockTimeoutId);
        }
      };

      const onAppBackgrounded = () => {
        if (document.visibilityState === 'hidden' && !hasNavigationResolved) {
          hasNavigationResolved = true;
          cleanupNavigationTrackers();
        }
      };

      const onPageUnloaded = () => {
        if (!hasNavigationResolved) {
          hasNavigationResolved = true;
          trackInAppNavigation({
            creator: creatorName,
            destinationUrl: targetUrl,
            platform: devicePlatformName,
          });
          cleanupNavigationTrackers();
        }
      };

      const handleSilentNavigationBlock = () => {
        if (!hasNavigationResolved && document.visibilityState === 'visible') {
          hasNavigationResolved = true;
          reportRoutingError({
            creator: creatorName,
            targetUrl,
            systemBrowserUrl,
            platform: devicePlatformName,
            errorType: 'Silent Block Timeout',
          });
          onSilentNavigationBlock?.();
          cleanupNavigationTrackers();
        }
      };

      document.addEventListener('visibilitychange', onAppBackgrounded);
      window.addEventListener('pagehide', onPageUnloaded);

      try {
        window.open(systemBrowserUrl, '_blank');

        silentNavigationBlockTimeoutId = setTimeout(
          handleSilentNavigationBlock,
          SILENT_BLOCK_TIMEOUT_MS
        );
      } catch (error) {
        hasNavigationResolved = true;
        reportRoutingError({
          creator: creatorName,
          targetUrl,
          systemBrowserUrl,
          platform: devicePlatformName,
          errorType: `DOM Exception: ${error.message}`,
        });
        cleanupNavigationTrackers();
      }
    },
    [creatorName, preferInAppBrowser, onSilentNavigationBlock, isInstagram]
  );

  return { handleLinkClick };
};

export default useInAppBrowser;
